import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, MapPin, Building2, GraduationCap } from "lucide-react";
import { FEATURED_LOCATIONS, locationToSlug } from "../lib/locations";
import BrandMark from "./BrandMark";
import CurrencySwitcher from "./CurrencySwitcher";

// Per-item color coding. Full class strings (kept static so Tailwind keeps them).
const NAV_STYLES = {
  emerald: {
    icon: "text-emerald-500",
    idle: "text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800",
    active: "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200",
  },
  indigo: {
    icon: "text-indigo-500",
    idle: "text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800",
    active: "bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200",
  },
  sky: {
    icon: "text-sky-500",
    idle: "text-sky-700 hover:bg-sky-50 hover:text-sky-800",
    active: "bg-sky-50 text-sky-800 ring-1 ring-sky-200",
  },
  rose: {
    icon: "text-rose-500",
    idle: "text-rose-700 hover:bg-rose-50 hover:text-rose-800",
    active: "bg-rose-50 text-rose-800 ring-1 ring-rose-200",
  },
  amber: {
    icon: "text-amber-500",
    idle: "text-amber-700 hover:bg-amber-50 hover:text-amber-800",
    active: "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
  },
  teal: {
    icon: "text-teal-500",
    idle: "text-teal-700 hover:bg-teal-50 hover:text-teal-800",
    active: "bg-teal-50 text-teal-800 ring-1 ring-teal-200",
  },
  violet: {
    icon: "text-violet-500",
    idle: "text-violet-700 hover:bg-violet-50 hover:text-violet-800",
    active: "bg-violet-50 text-violet-800 ring-1 ring-violet-200",
  },
  fuchsia: {
    icon: "text-fuchsia-500",
    idle: "text-fuchsia-700 hover:bg-fuchsia-50 hover:text-fuchsia-800",
    active: "bg-fuchsia-50 text-fuchsia-800 ring-1 ring-fuchsia-200",
  },
};

const PRIMARY_LINKS = [
  { label: "Home", path: "/", color: "emerald" },
  { label: "Teacher Training", path: "/yoga-teacher-training", color: "emerald" },
  { label: "Online Courses", path: "/online-courses", color: "emerald" },
  { label: "Prenatal Yoga", path: "/prenatal-yoga", color: "emerald" },
];

const SECONDARY_LINKS = [
  { label: "About", path: "/about", color: "emerald" },
  { label: "Contact Us", path: "/contact", color: "emerald" },
  { label: "Blog", path: "/blog", color: "emerald" },
];

const JOIN_LINKS = [
  {
    label: "Become an instructor",
    path: "/become-an-instructor",
    icon: GraduationCap,
  },
  {
    label: "Become a partner",
    path: "/become-a-partner",
    icon: Building2,
  },
];

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [destOpen, setDestOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const location = useLocation();
  const destRef = useRef(null);
  const joinRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    setDestOpen(false);
    setJoinOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (destRef.current && !destRef.current.contains(e.target)) {
        setDestOpen(false);
      }
      if (joinRef.current && !joinRef.current.contains(e.target)) {
        setJoinOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isActive = (path) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-md" : "bg-white/90 backdrop-blur-sm shadow-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <BrandMark />
            <span className="font-bold text-gray-900 text-lg leading-tight">
              Yoga Retreats <span className="text-emerald-600">Booking</span>
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {/* Home */}
            {(() => {
              const item = PRIMARY_LINKS[0];
              const s = NAV_STYLES[item.color];
              const active = isActive(item.path);
              return (
                <Link
                  to={item.path}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    active ? s.active : s.idle
                  }`}
                >
                  {item.label}
                </Link>
              );
            })()}

            {/* Destinations dropdown */}
            <div className="relative" ref={destRef}>
              <button
                type="button"
                onClick={() => {
                  setDestOpen((v) => !v);
                  setJoinOpen(false);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  location.pathname.startsWith("/yoga-retreats-in/")
                    ? NAV_STYLES.emerald.active
                    : NAV_STYLES.emerald.idle
                }`}
                aria-haspopup="true"
                aria-expanded={destOpen}
              >
                Destinations
                <ChevronDown size={14} className={`transition-transform ${destOpen ? "rotate-180" : ""}`} />
              </button>

              {destOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                  {FEATURED_LOCATIONS.map((loc) => (
                    <Link
                      key={loc}
                      to={`/yoga-retreats-in/${locationToSlug(loc)}`}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 transition-colors"
                    >
                      <MapPin size={14} className="text-emerald-500" />
                      Yoga Retreats in {loc}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Remaining primary links */}
            {PRIMARY_LINKS.slice(1).map((item) => {
              const s = NAV_STYLES[item.color];
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    active ? s.active : s.idle
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            {/* Divider between primary and secondary links */}
            <span className="w-px h-5 bg-gray-200 mx-1" aria-hidden="true" />

            {/* Secondary links */}
            {SECONDARY_LINKS.map((item) => {
              const s = NAV_STYLES[item.color];
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                    active ? s.active : s.idle
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}

            <CurrencySwitcher className="ml-2" />

            <div className="relative ml-1" ref={joinRef}>
              <button
                type="button"
                onClick={() => {
                  setJoinOpen((v) => !v);
                  setDestOpen(false);
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  joinOpen ||
                  location.pathname.startsWith("/become-an-instructor") ||
                  location.pathname.startsWith("/become-a-partner")
                    ? NAV_STYLES.emerald.active
                    : NAV_STYLES.emerald.idle
                }`}
                aria-haspopup="true"
                aria-expanded={joinOpen}
              >
                Join us
                <ChevronDown
                  size={14}
                  className={`transition-transform ${joinOpen ? "rotate-180" : ""}`}
                />
              </button>

              {joinOpen && (
                <div className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-gray-100 bg-white py-2 shadow-lg">
                  {JOIN_LINKS.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium transition-colors ${
                          active
                            ? "bg-emerald-50 text-emerald-800"
                            : "text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                        }`}
                      >
                        <Icon size={15} className="shrink-0 text-emerald-500" />
                        {item.label}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>

          {/* Mobile / tablet menu button */}
          <button
            className="lg:hidden rounded-lg p-2 text-gray-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle navigation menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="border-t border-gray-100 bg-white/98 backdrop-blur-md lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-3">
            {PRIMARY_LINKS.map((link) => {
              const s = NAV_STYLES[link.color];
              const active = isActive(link.path);
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm font-semibold transition-colors ${
                    active ? s.active : s.idle
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}

            <div className="mt-1 flex flex-col gap-1 border-t border-gray-100 pt-1">
              {SECONDARY_LINKS.map((link) => {
                const s = NAV_STYLES[link.color];
                const active = isActive(link.path);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm font-semibold transition-colors ${
                      active ? s.active : s.idle
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>

            <div className="mt-2 border-t border-gray-100 px-4 pt-2">
              <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-emerald-600">
                Destinations
              </p>
            </div>
            {FEATURED_LOCATIONS.map((loc) => (
              <Link
                key={loc}
                to={`/yoga-retreats-in/${locationToSlug(loc)}`}
                className="flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium text-gray-700 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
              >
                <MapPin size={14} className="text-emerald-500" />
                Yoga Retreats in {loc}
              </Link>
            ))}

            <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 px-4 pt-3">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Currency
              </span>
              <CurrencySwitcher />
            </div>

            <div className="mt-3 space-y-1 border-t border-gray-100 pt-3">
              <p className="px-4 pb-1 text-[10px] font-bold uppercase tracking-widest text-emerald-600">
                Join us
              </p>
              {JOIN_LINKS.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                      active
                        ? "bg-emerald-50 text-emerald-800"
                        : "text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                    }`}
                  >
                    <Icon size={15} className="shrink-0 text-emerald-500" />
                    {item.label}
                  </Link>
                );
              })}
            </div>

            <Link
              to="/"
              className="mt-2 rounded-lg bg-emerald-600 px-4 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
            >
              Browse Retreats
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}

export default Header;
